import {
    Button,
    Text,
    Flex,
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Divider
} from '@chakra-ui/react';
import { useEffect, useState, useMemo } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from "react-i18next";

const TicketsCart = ({ tickets, onBuy, cart, finalCart = false }) => {
    const { t } = useTranslation("default");

    useEffect(() => {
        if (cart && cart.quantities) {
            const corrected = { ...cart.quantities };
            tickets.forEach(ticket => {
                const maxPerOrder = Number(ticket.maxTicketsPerOrder) || 0;
                const limit = maxPerOrder > 0 
                    ? Math.min(ticket.availableQuantity, maxPerOrder) 
                    : ticket.availableQuantity;
                if (corrected[ticket.id] > limit) {
                    corrected[ticket.id] = limit;
                }
            });
            setQuantities(corrected);
        }
    }, [cart, tickets]);

    const [quantities, setQuantities] = useState(() =>
        tickets.reduce((acc, ticket) => {
            if (!finalCart) {
                acc[ticket.id] = 0;
            }
            return acc;
        }, {})
    );

    const handleChangeQuantity = (ticketId, delta) => {
        setQuantities(prev => {
            const ticket = tickets.find(t => t.id === ticketId);
            const maxPerOrder = Number(ticket.maxTicketsPerOrder) || 0;
            const limit = maxPerOrder > 0 
                ? Math.min(ticket.availableQuantity, maxPerOrder) 
                : ticket.availableQuantity;
            const newQty = Math.max(0, Math.min(
                limit,
                (prev[ticketId] || 0) + delta
            ));
            return { ...prev, [ticketId]: newQty };
        });
    };

    const grossPrice = (ticket) =>
        ticket.price * (1 + ticket.vatPercentage / 100);

    const totalPrice = useMemo(() =>
        tickets.reduce((sum, ticket) => {
            return sum + (grossPrice(ticket) * (quantities[ticket.id] || 0));
        }, 0), [quantities, tickets]);

    const summary = useMemo(() =>
        tickets.filter(t => quantities[t.id] > 0).map(t => ({
            name: t.name,
            quantity: quantities[t.id]
        })), [quantities, tickets]);

    const formatDate = (isoDate) => {
        if (!isoDate) return '—';
        return dayjs(isoDate).format('DD MMMM YYYY');
    };

    const formattedDate = (date) => {
        const formattedDate = new Intl.DateTimeFormat('pl-PL', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(date);
        return formattedDate;
    }

    const isAvailable = (ticket) => {
        if (ticket.availableQuantity <= 0) return false;
        const now = dayjs();
        if (ticket.saleStart && now.isBefore(dayjs(ticket.saleStart))) return false;
        if (ticket.saleEnd && now.isAfter(dayjs(ticket.saleEnd))) return false;
        return true;
    };

    const isDisabled = (ticket) => {
        if (ticket.availableQuantity <= 0) return true;
        const now = dayjs();
        if (ticket.saleStart && now.isBefore(dayjs(ticket.saleStart))) return true;
        if (ticket.saleEnd && now.isAfter(dayjs(ticket.saleEnd))) return true;
        return false;
    }

    const getAvailabilityLabel = (ticket) => {
        if (ticket.availableQuantity <= 0) return t('ticket.sold');
        const now = dayjs();
        if (!ticket.saleStart) {
            return '-';
        }
        if (ticket.saleStart && now.isBefore(dayjs(ticket.saleStart)))
            return <Flex flexDirection="column" gap="2px">
                <Text fontWeight="400" fontSize="18px">
                    {t('unavailable')}
                </Text>
                <Text>
                    {t('cart.ticket.from')} {formattedDate(new Date(ticket.saleStart))}
                </Text>
            </Flex>;
        if (ticket.saleEnd && now.isAfter(dayjs(ticket.saleEnd)))
            return t('unavailable');
        return `${t('cart.ticket.from')} ${formattedDate(new Date(ticket.saleEnd))}`;
    };

    return (
        <Box bg="#F3F3F3" p="24px" borderRadius="8px" w="100%" className="ticket-box">

            {/* DESKTOP/TABLET -> tabela */}
            <Box display={{ base: 'none', md: 'block' }}>
                <Table mb={4}>
                    <Thead>
                        <Tr>
                            <Th>{t('cart.table.th.kind')}</Th>
                            <Th>{t('cart.table.th.price')}</Th>
                            <Th>{t('cart.table.th.available_until')}</Th>
                            <Th textAlign="center"> </Th>
                        </Tr>
                    </Thead>
                    <Tbody gap="16px">
                        {tickets.map(ticket => (
                            <Tr key={ticket.id} py="16px" borderBottom="16px solid #F3F3F3;" className={isDisabled(ticket) ? 'disabled-ticket' : ''}>
                                <Td bg="white">{ticket.name}</Td>
                                <Td bg="white">{grossPrice(ticket).toFixed(2)} {t('currency.pln')}</Td>
                                <Td bg="white">{getAvailabilityLabel(ticket)}</Td>
                                <Td bg="white">
                                    <Flex justify="center" align="center" gap={2}>
                                        <Button bgColor="#089A7A" fontFamily="Poppins" fontSize="18px" fontWeight="500" borderLeftRadius="100px"
                                            onClick={() => handleChangeQuantity(ticket.id, -1)}
                                            isDisabled={quantities[ticket.id] <= 0}>
                                            -
                                        </Button>
                                        <Text minW="20px" textAlign="center" fontFamily="Nunito" fontSize="20px" fontWeight="600">
                                            {quantities[ticket.id]}
                                        </Text>
                                        <Button bgColor="#089A7A" fontFamily="Poppins" fontSize="18px" fontWeight="500" borderRightRadius="100px"
                                            onClick={() => handleChangeQuantity(ticket.id, 1)}
                                            isDisabled={!isAvailable(ticket) || quantities[ticket.id] >= ((Number(ticket.maxTicketsPerOrder) || 0) > 0 ? Math.min(ticket.availableQuantity, Number(ticket.maxTicketsPerOrder)) : ticket.availableQuantity)}>
                                            +
                                        </Button>
                                    </Flex>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>

            {/* MOBILE -> lista kart */}
            <Box display={{ base: 'block', md: 'none' }}>
                <Flex direction="column" gap="12px" mb={4}>
                    {tickets.map(ticket => {
                        const qty = quantities[ticket.id] || 0;
                        const disabled = isDisabled(ticket);
                        const maxPerOrder = Number(ticket.maxTicketsPerOrder) || 0;
                        const limit = maxPerOrder > 0 ? Math.min(ticket.availableQuantity, maxPerOrder) : ticket.availableQuantity;

                        return (
                            <Box
                                key={ticket.id}
                                bg="white"
                                borderRadius="8px"
                                p="12px"
                                opacity={disabled ? 0.6 : 1}
                            >
                                {/* wiersz: nazwa + stepper */}
                                <Flex align="center" justify="space-between" gap="12px" mb="6px">
                                    <Text fontWeight="700">{ticket.name}</Text>
                                    <Flex align="center" gap={2}>
                                        <Button
                                            size="sm" bg="#089A7A" color="white" borderLeftRadius="999px" minW="32px"
                                            onClick={() => handleChangeQuantity(ticket.id, -1)}
                                            isDisabled={qty <= 0}
                                        >
                                            –
                                        </Button>
                                        <Text minW="20px" textAlign="center" fontWeight="700">{qty}</Text>
                                        <Button
                                            size="sm" bg="#089A7A" color="white" borderRightRadius="999px" minW="32px"
                                            onClick={() => handleChangeQuantity(ticket.id, 1)}
                                            isDisabled={!isAvailable(ticket) || qty >= limit}
                                        >
                                            +
                                        </Button>
                                    </Flex>
                                </Flex>

                                {/* wiersz: cena */}
                                <Flex justify="space-between" mb="4px">
                                    <Text color="gray.600">{t('cart.table.th.price')}</Text>
                                    <Text fontWeight="600">{grossPrice(ticket).toFixed(2)} {t('currency.pln')}</Text>
                                </Flex>

                                {/* wiersz: dostępność */}
                                <Flex justify="space-between">
                                    <Text color="gray.600">{t('cart.table.th.available_until')}</Text>
                                    <Box textAlign="right">
                                        {getAvailabilityLabel(ticket)}
                                    </Box>
                                </Flex>
                            </Box>
                        );
                    })}
                </Flex>
            </Box>

            <Divider my={4} borderColor={'#BFBFBF'} />

            <Flex justify="space-between" mt={'32px'} direction={{ base: 'column', md: 'row' }} gap={{ base: 4, md: 0 }}>
                <Flex className="summary" direction="column" gap="16px">
                    <Text className="title">{t('summary')}</Text>
                    {summary.length === 0 ? (
                        <Text>{t('ticket.no')}</Text>
                    ) : (
                        summary.map((item, index) => (
                            <Text key={index} className="item">{item.quantity}× {item.name}</Text>
                        ))
                    )}
                </Flex>
                <Flex flexDirection="column" gap="17px">
                    <Flex gap="16px" align="center" justify={{ base: 'space-between', md: 'flex-start' }}>
                        <Text className="sum">
                            {t('to_be_pay')}
                        </Text>
                        <Text className="value" color="#089A7A">
                            {totalPrice.toFixed(2)} {t('currency.pln')}
                        </Text>
                    </Flex>
                    <Button
                        w={{ base: '100%', md: 'auto' }}
                        bg="#089A7A"
                        color="white"
                        size="lg"
                        isDisabled={summary.length === 0}
                        onClick={() => onBuy({ quantities, totalPrice })}
                        _hover={{ bg: "#067a63" }}
                        gap="10px"
                    >
                        <svg width="21" height="17" viewBox="0 0 21 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13.5 1.5V3.5M13.5 7.5V9.5M13.5 13.5V15.5M3.5 1.5C2.96957 1.5 2.46086 1.71071 2.08579 2.08579C1.71071 2.46086 1.5 2.96957 1.5 3.5V6.5C2.03043 6.5 2.53914 6.71071 2.91421 7.08579C3.28929 7.46086 3.5 7.96957 3.5 8.5C3.5 9.03043 3.28929 9.53914 2.91421 9.91421C2.53914 10.2893 2.03043 10.5 1.5 10.5V13.5C1.5 14.0304 1.71071 14.5391 2.08579 14.9142C2.46086 15.2893 2.96957 15.5 3.5 15.5H17.5C18.0304 15.5 18.5391 15.2893 18.9142 14.9142C19.2893 14.5391 19.5 14.0304 19.5 13.5V10.5C18.9696 10.5 18.4609 10.2893 18.0858 9.91421C17.7107 9.53914 17.5 9.03043 17.5 8.5C17.5 7.96957 17.7107 7.46086 18.0858 7.08579C18.4609 6.71071 18.9696 6.5 19.5 6.5V3.5C19.5 2.96957 19.2893 2.46086 18.9142 2.08579C18.5391 1.71071 18.0304 1.5 17.5 1.5H3.5Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {t('ticket.buy')}
                    </Button>
                </Flex>
            </Flex>
        </Box>
    );
};

export default TicketsCart;
